import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Minus, Trash2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import StockSelect from "@/components/StockSelect";
import { fetchSlittingRmOptions, fetchSlittingPriorityTaken } from "@/lib/api";

// Cascading options from RM still free for planning (status "received").
const isFree = (r) => (r.status || "received") === "received";
const stockHint = (rows) => {
  const free = rows.filter(isFree);
  if (!free.length) {
    const held = rows.filter((r) => r.status === "received_hold").reduce((a, r) => a + r.coils, 0);
    return held ? `${held} on hold` : "not received yet";
  }
  const coils = free.reduce((a, r) => a + r.coils, 0);
  const kg = free.reduce((a, r) => a + r.kg, 0);
  return `${coils} coil${coils !== 1 ? "s" : ""} · ${(kg / 1000).toFixed(2)} t`;
};
// Options with no "received" coils are shown greyed out (on hold / not yet received).
const groupOpts = (rows, keyFn, labelFn, sortFn) => {
  const m = new Map();
  rows.forEach((r) => { const k = keyFn(r); m.set(k, [...(m.get(k) || []), r]); });
  return [...m.entries()].sort(sortFn)
    .map(([k, rs]) => ({ value: k, label: labelFn(k), hint: stockHint(rs), disabled: !rs.some(isFree) }))
    .sort((a, b) => Number(a.disabled) - Number(b.disabled));
};
const sameNum = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-6;
const gradeOptions = (opts) => groupOpts(opts, (r) => r.grade, (k) => k, ([a], [b]) => a.localeCompare(b));
const thicknessOptions = (opts, grade) =>
  groupOpts(opts.filter((r) => r.grade === grade && r.thickness_mm), (r) => String(Number(r.thickness_mm)),
    (k) => `${Number(k).toFixed(2)} mm`, ([a], [b]) => Number(a) - Number(b));
const widthOptions = (opts, grade, thk) =>
  thk === "" ? [] : groupOpts(opts.filter((r) => r.grade === grade && sameNum(r.thickness_mm, thk) && r.width_mm),
    (r) => String(Number(r.width_mm)), (k) => `${k} mm`, ([a], [b]) => Number(a) - Number(b));

/**
 * Multi-line slitting plan form. Used for both Create (no existingPlan) and Edit (existingPlan provided).
 * On Edit, lines that already have mother_coil_id are LOCKED (rendered read-only at top).
 */
// Common slit widths used on the floor — tap to fill the first empty slit-width input on this line.
const COMMON_WIDTHS = [
  63.5, 68.5, 70, 76.2, 78, 79, 86, 90, 95.2, 96.8, 97, 100, 105, 110, 114, 114.3, 120.7, 130, 133,
];

// Stable unique ids for form rows so React keys survive add/remove/reorder.
let _uidCounter = 0;
const uid = (prefix) => `${prefix}-${++_uidCounter}-${Date.now()}`;

const emptyLine = () => ({
  _uid: uid("ln"),
  target_grade: "",
  target_thickness_mm: "",
  target_width_mm: "",
  tolerance_mm: "-0.1",
  priority_no: "",
  num_parts: "1",
  slit_plan: [{ _uid: uid("sl"), slit_width_mm: "", count: 1 }],
});

const sanitizeLines = (lines) =>
  lines.map((ln) => ({
    target_grade: (ln.target_grade || "").trim(),
    target_thickness_mm: parseFloat(ln.target_thickness_mm) || 0,
    target_width_mm: parseFloat(ln.target_width_mm) || 0,
    tolerance_mm: Number.isFinite(parseFloat(ln.tolerance_mm)) ? parseFloat(ln.tolerance_mm) : -0.1,
    priority_no: parseInt(ln.priority_no, 10) || null,
    num_parts: parseInt(ln.num_parts, 10) || 0,
    slit_plan: (ln.slit_plan || [])
      .map((r) => ({ slit_width_mm: parseFloat(r.slit_width_mm) || 0, count: parseInt(r.count, 10) || 0 }))
      .filter((r) => r.slit_width_mm > 0 && r.count > 0),
  }));

const validateLine = (ln, idx) => {
  if (!ln.target_grade?.trim()) return `Line ${idx + 1}: grade required`;
  if (!(ln.target_thickness_mm > 0)) return `Line ${idx + 1}: thickness must be > 0`;
  if (!(ln.target_width_mm > 0)) return `Line ${idx + 1}: target width must be > 0`;
  if (!(ln.num_parts >= 1 && ln.num_parts <= 20)) return `Line ${idx + 1}: number of parts must be between 1 and 20`;
  if (!ln.slit_plan?.length) return `Line ${idx + 1}: add at least one slit`;
  const total = ln.slit_plan.reduce((acc, r) => acc + r.slit_width_mm * r.count, 0);
  if (total <= 0) return `Line ${idx + 1}: total slit width must be > 0`;
  if (total > ln.target_width_mm + 0.001) return `Line ${idx + 1}: slit widths ${total}mm exceed target ${ln.target_width_mm}mm`;
  return null;
};

const SlittingPlanForm = ({ existingPlan = null, onSubmit, onCancel, submitting = false }) => {
  const isEdit = !!existingPlan;
  const lockedLines = isEdit ? existingPlan.lines.filter((l) => l.mother_coil_id) : [];

  const initLines = isEdit
    ? existingPlan.lines.filter((l) => !l.mother_coil_id).map((l) => ({
        _uid: uid("ln"),
        target_grade: l.target_grade,
        target_thickness_mm: l.target_thickness_mm,
        target_width_mm: l.target_width_mm,
        tolerance_mm: String(l.tolerance_mm ?? -0.1),
        priority_no: l.priority_no ? String(l.priority_no) : "",
        num_parts: String(l.num_parts ?? 1),
        slit_plan: l.slit_plan.map((r) => ({ _uid: uid("sl"), slit_width_mm: r.slit_width_mm, count: r.count })),
      }))
    : [emptyLine()];

  const [lines, setLines] = useState(initLines.length ? initLines : [emptyLine()]);
  const [notes, setNotes] = useState(existingPlan?.notes || "");
  const [priority, setPriority] = useState(existingPlan?.priority || "normal");
  const { data: takenPriorities = [] } = useQuery({
    queryKey: ["slitting-priority-taken", existingPlan?.id || "new"],
    queryFn: () => fetchSlittingPriorityTaken(existingPlan?.id),
    staleTime: 10_000,
  });
  // Numbers used by other open plans + this plan's locked (scanned) lines.
  const takenBy = new Map(takenPriorities.map((t) => [Number(t.priority_no), t.plan_number]));
  lockedLines.forEach((l) => l.priority_no && takenBy.set(Number(l.priority_no), "a scanned coil in this plan"));
  const priorityError = (lineIdx) => {
    const n = parseInt(lines[lineIdx]?.priority_no, 10);
    if (!n) return "";
    if (n < 1) return "Must be 1 or more";
    if (takenBy.has(n)) return `P${n} already taken by ${takenBy.get(n)}`;
    if (lines.some((l, i) => i !== lineIdx && parseInt(l.priority_no, 10) === n)) return `P${n} is used twice in this plan`;
    return "";
  };
  const nextFree = (() => {
    const used = new Set([...takenBy.keys(), ...lines.map((l) => parseInt(l.priority_no, 10)).filter(Boolean)]);
    let n = 1;
    while (used.has(n)) n += 1;
    return n;
  })();
  const { data: rmOptions = [] } = useQuery({
    queryKey: ["slitting-rm-options"],
    queryFn: fetchSlittingRmOptions,
    staleTime: 30_000,
  });

  const updateLine = (idx, patch) =>
    setLines((curr) => curr.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  const updateSlit = (lineIdx, slitIdx, patch) =>
    setLines((curr) =>
      curr.map((ln, i) =>
        i === lineIdx
          ? { ...ln, slit_plan: ln.slit_plan.map((r, ri) => (ri === slitIdx ? { ...r, ...patch } : r)) }
          : ln,
      ),
    );
  const addSlit = (lineIdx) =>
    setLines((curr) =>
      curr.map((ln, i) => (i === lineIdx ? { ...ln, slit_plan: [...ln.slit_plan, { _uid: uid("sl"), slit_width_mm: "", count: 1 }] } : ln)),
    );
  const removeSlit = (lineIdx, slitIdx) =>
    setLines((curr) =>
      curr.map((ln, i) =>
        i === lineIdx ? { ...ln, slit_plan: ln.slit_plan.filter((_, ri) => ri !== slitIdx) } : ln,
      ),
    );

  // Tap a quick-pick chip → fills first empty slit-width input on that line, else appends a new row.
  const pickWidthForLine = (lineIdx, w) => {
    setLines((curr) =>
      curr.map((ln, i) => {
        if (i !== lineIdx) return ln;
        const emptyIdx = ln.slit_plan.findIndex((r) => !r.slit_width_mm && r.slit_width_mm !== 0);
        if (emptyIdx >= 0) {
          return {
            ...ln,
            slit_plan: ln.slit_plan.map((r, ri) => (ri === emptyIdx ? { ...r, slit_width_mm: w } : r)),
          };
        }
        return { ...ln, slit_plan: [...ln.slit_plan, { _uid: uid("sl"), slit_width_mm: w, count: 1 }] };
      }),
    );
  };

  const addLine = () => setLines((curr) => [...curr, emptyLine()]);
  const removeLine = (idx) => setLines((curr) => curr.filter((_, i) => i !== idx));

  const lineSummary = (ln) => {
    const total = (ln.slit_plan || []).reduce((acc, r) => acc + (parseFloat(r.slit_width_mm) || 0) * (parseInt(r.count, 10) || 0), 0);
    const count = (ln.slit_plan || []).reduce((acc, r) => acc + (parseInt(r.count, 10) || 0), 0);
    const width = parseFloat(ln.target_width_mm) || 0;
    const overflow = total > width + 0.001;
    const trim = overflow ? 0 : Math.max(0, width - total);
    const usedPct = width > 0 ? Math.min(100, (total / width) * 100) : 0;
    return { total, count, width, overflow, trim, usedPct };
  };

  const handleSubmit = () => {
    const cleaned = sanitizeLines(lines);
    for (let i = 0; i < cleaned.length; i++) {
      const err = validateLine(cleaned[i], i) || (priorityError(i) && `Line ${i + 1}: ${priorityError(i)}`);
      if (err) {
        alert(err);
        return;
      }
    }
    // Server expects scanned (locked) lines first, unchanged, then the editable ones.
    const locked = lockedLines.map((l) => ({
      target_grade: l.target_grade, target_thickness_mm: l.target_thickness_mm, target_width_mm: l.target_width_mm,
      slit_plan: (l.slit_plan || []).map((r) => ({ slit_width_mm: r.slit_width_mm, count: r.count })),
      tolerance_mm: l.tolerance_mm ?? -0.1, num_parts: l.num_parts || 1, priority_no: l.priority_no || null,
    }));
    onSubmit({ lines: [...locked, ...cleaned], notes, priority });
  };

  return (
    <div data-testid="plan-form" className="space-y-5">
      {isEdit && lockedLines.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-amber-900">
            {lockedLines.length} line(s) already have an RM scanned and are locked.
            Unbind the RM(s) from the plan detail screen if you need to edit them.
          </p>
        </div>
      )}

      {lines.map((ln, lineIdx) => {
        const { total, count, width, overflow, trim, usedPct } = lineSummary(ln);
        return (
          <div key={ln._uid} data-testid={`plan-line-${lineIdx}`} className="border border-slate-200 rounded-sm p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-sm font-bold text-slate-900 tracking-wide">RM COIL #{lockedLines.length + lineIdx + 1}</p>
              {lines.length > 1 && (
                <button
                  data-testid={`remove-line-${lineIdx}`}
                  onClick={() => removeLine(lineIdx)}
                  className="text-red-600 text-xs flex items-center gap-1 hover:underline"
                  type="button"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Grade *</label>
                <div className="mt-1">
                  <StockSelect
                    testId={`line-${lineIdx}-grade`}
                    value={ln.target_grade}
                    options={gradeOptions(rmOptions)}
                    placeholder="Select grade"
                    onChange={(v) => updateLine(lineIdx, { target_grade: v.toUpperCase(), target_thickness_mm: "", target_width_mm: "" })}
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Thickness mm *</label>
                <div className="mt-1">
                  <StockSelect
                    testId={`line-${lineIdx}-thickness`}
                    inputType="number"
                    value={ln.target_thickness_mm}
                    options={thicknessOptions(rmOptions, ln.target_grade)}
                    resetKey={ln.target_grade}
                    disabled={!ln.target_grade}
                    placeholder={ln.target_grade ? "Select thickness" : "Pick grade first"}
                    onChange={(v) => updateLine(lineIdx, { target_thickness_mm: v, target_width_mm: "" })}
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">RM Width mm *</label>
                <div className="mt-1">
                  {(() => {
                    const wOpts = widthOptions(rmOptions, ln.target_grade, ln.target_thickness_mm);
                    return (
                      <StockSelect
                        testId={`line-${lineIdx}-width`}
                        inputType="number"
                        value={ln.target_width_mm}
                        options={wOpts}
                        allowCustom={wOpts.length === 0}
                        resetKey={`${ln.target_grade}|${ln.target_thickness_mm}`}
                        disabled={!ln.target_thickness_mm}
                        placeholder={ln.target_thickness_mm ? "Select width" : "Pick thickness first"}
                        onChange={(v) => updateLine(lineIdx, { target_width_mm: v })}
                      />
                    );
                  })()}
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Priority No.</label>
                <input
                  data-testid={`line-${lineIdx}-priority-no`}
                  type="number" step="1" min="1" placeholder={`Next free: ${nextFree}`}
                  value={ln.priority_no}
                  onChange={(e) => updateLine(lineIdx, { priority_no: e.target.value })}
                  className={`mt-1 w-full h-10 px-2 border rounded-sm text-sm font-mono ${priorityError(lineIdx) ? "border-red-500 bg-red-50" : "border-slate-300"}`}
                />
                {priorityError(lineIdx) ? (
                  <p data-testid={`line-${lineIdx}-priority-error`} className="text-[11px] text-red-600 mt-1">{priorityError(lineIdx)}</p>
                ) : !ln.priority_no ? (
                  <button type="button" data-testid={`line-${lineIdx}-priority-use-next`} onClick={() => updateLine(lineIdx, { priority_no: String(nextFree) })}
                    className="text-[11px] text-blue-700 hover:underline mt-1">Use P{nextFree}</button>
                ) : null}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Tolerance mm</label>
                <input
                  data-testid={`line-${lineIdx}-tolerance`}
                  type="number" step="0.01"
                  value={ln.tolerance_mm}
                  onChange={(e) => updateLine(lineIdx, { tolerance_mm: e.target.value })}
                  className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Number of Parts</label>
                <input
                  data-testid={`line-${lineIdx}-parts`}
                  type="number" step="1" min="1" max="20"
                  value={ln.num_parts}
                  onChange={(e) => updateLine(lineIdx, { num_parts: e.target.value })}
                  className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
                />
              </div>
            </div>

            <div className="mt-4">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Slit plan (widths × count)</p>

              {/* Quick-pick chips — tap to fill the first empty width on this line */}
              <div className="mb-3" data-testid={`line-${lineIdx}-quickpicks`}>
                <p className="text-[9px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-1.5">Common widths (mm)</p>
                <div className="flex flex-wrap gap-1">
                  {COMMON_WIDTHS.map((w) => (
                    <button
                      key={w}
                      type="button"
                      data-testid={`line-${lineIdx}-quickpick-${w}`}
                      onClick={() => pickWidthForLine(lineIdx, w)}
                      className="px-2 h-7 text-[11px] font-mono border border-slate-200 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 rounded-sm transition-colors"
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {ln.slit_plan.map((row, slitIdx) => (
                  <div key={row._uid} className="flex items-center gap-2">
                    <input
                      data-testid={`line-${lineIdx}-slit-${slitIdx}-width`}
                      type="number" step="1" min="1" placeholder="width mm"
                      value={row.slit_width_mm}
                      onChange={(e) => updateSlit(lineIdx, slitIdx, { slit_width_mm: e.target.value })}
                      className="h-10 px-2 w-32 border border-slate-300 rounded-sm text-sm font-mono"
                    />
                    <span className="text-slate-500">×</span>
                    <input
                      data-testid={`line-${lineIdx}-slit-${slitIdx}-count`}
                      type="number" step="1" min="1" placeholder="count"
                      value={row.count}
                      onChange={(e) => updateSlit(lineIdx, slitIdx, { count: e.target.value })}
                      className="h-10 px-2 w-24 border border-slate-300 rounded-sm text-sm font-mono"
                    />
                    {ln.slit_plan.length > 1 && (
                      <button
                        type="button"
                        data-testid={`line-${lineIdx}-remove-slit-${slitIdx}`}
                        onClick={() => removeSlit(lineIdx, slitIdx)}
                        className="text-red-600 p-1 hover:bg-red-50 rounded"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  data-testid={`line-${lineIdx}-add-slit`}
                  onClick={() => addSlit(lineIdx)}
                  className="text-xs text-slate-700 flex items-center gap-1 hover:underline"
                >
                  <Plus className="h-3 w-3" /> Add slit width
                </button>
              </div>
            </div>

            <div className="mt-3 space-y-1.5">
              {/* Visual usage bar */}
              <div
                data-testid={`line-${lineIdx}-usage-bar`}
                className="relative h-2.5 w-full bg-slate-100 rounded-sm overflow-hidden border border-slate-200"
                aria-label={`${total} of ${width}mm used, ${trim}mm trim`}
              >
                <div
                  className={`h-full transition-all ${overflow ? "bg-red-500" : "bg-slate-900"}`}
                  style={{ width: `${overflow ? 100 : usedPct}%` }}
                />
              </div>
              {/* Stats row */}
              <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span data-testid={`line-${lineIdx}-used`} className={overflow ? "text-red-700 font-bold" : "text-slate-700"}>
                    <span className="text-[10px] tracking-wider uppercase text-slate-500 font-semibold mr-1">Used</span>
                    <span className="font-mono font-semibold">{total}</span>
                    <span className="text-slate-400 mx-1">/</span>
                    <span className="font-mono">{width || "—"}</span>
                    <span className="text-slate-400 ml-0.5">mm</span>
                  </span>
                  <span data-testid={`line-${lineIdx}-trim`} className={overflow ? "text-red-700 font-bold" : trim === 0 && width > 0 ? "text-green-700 font-semibold" : "text-slate-600"}>
                    <span className="text-[10px] tracking-wider uppercase text-slate-500 font-semibold mr-1">Trim</span>
                    <span className="font-mono font-semibold">{overflow ? "—" : trim.toFixed(trim % 1 === 0 ? 0 : 1)}</span>
                    <span className="text-slate-400 ml-0.5">mm</span>
                  </span>
                  {overflow && <span className="text-[10px] tracking-widest uppercase font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-sm">Overflow</span>}
                </div>
                <span className="text-slate-500">{count} SFG{count !== 1 ? "s" : ""} will be issued</span>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        data-testid="add-rm-line-btn"
        onClick={addLine}
        className="w-full h-12 border-2 border-dashed border-slate-300 rounded-sm text-slate-700 hover:bg-slate-50 hover:border-slate-900 flex items-center justify-center gap-2 font-semibold transition-colors"
      >
        <Plus className="h-4 w-4" /> Add another RM coil
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Notes</label>
          <input
            data-testid="plan-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any context for the slitter…"
            className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Priority (director only)</label>
          <select
            data-testid="plan-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm"
          >
            <option value="normal">Normal</option>
            <option value="urgent">🚩 Urgent</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="plan-form-cancel">
          Cancel
        </Button>
        <Button data-testid="plan-form-submit" onClick={handleSubmit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : (isEdit ? "Save changes" : "Create plan & issue SFGs")}
        </Button>
      </div>
    </div>
  );
};

export default SlittingPlanForm;
